import './ShipmentAddressForm.scss';

import { LoadingOutlined } from '@ant-design/icons';
import { Alert, Button, Form, Input, Table, Tag } from 'antd';
import { FormProps } from 'antd/lib';
import { useTranslation } from 'react-i18next';

import AntdSelect from '@/components/general/AntdSelect/AntdSelect';
import { useLogisticAddressForm } from '@/hooks/useLogisticAddressForm';
import { phoneNumberMeta } from '@/utils/phoneNumbers';

interface ShipmentAddressFormProps {
  formProps: FormProps;
  initialValuesModified?: any;
  setSaveAddress: (value: boolean) => void;
  initialValuesSetted?: boolean;
}

const ShipmentAddressForm = (props: ShipmentAddressFormProps) => {
  const { t } = useTranslation();
  const { formProps, initialValuesModified, setSaveAddress, initialValuesSetted } = props;
  const {
    company,
    pais,
    provincia,
    selectedAddress,
    tableProps,
    dataSource,
    paisesSelectProps,
    provinciasSelectProps,
    localidadesSelectProps,
    countryCode,
    isBrasilSelected,
    localidadesOptions,
    handleSavedAddressChange,
    handleLocalidadChange,
    cepLookupState,
    enableBrazilManualLocation,
  } = useLogisticAddressForm({
    formProps,
    initialValuesModified,
    initialValuesSetted,
    setSaveAddress,
  });
  const postalCodeLabel = isBrasilSelected ? 'CEP' : t('Código postal');
  const canEditLocation = !isBrasilSelected || cepLookupState.canEditLocationManually;

  return (
    <div className="ShipmentAddressForm">
      {!!company && dataSource && dataSource.length > 0 && (
        <Table
          {...tableProps}
          className="ShipmentAddressFormTable"
          size="small"
          rowKey="id"
          scroll={{ x: 500, y: 150 }}
          pagination={false}
          rowSelection={{
            type: 'radio',
            selectedRowKeys: selectedAddress?.id ? [selectedAddress.id] : [],
            onChange: handleSavedAddressChange,
          }}>
          <Table.Column dataIndex="description" title={t('Direcciones guardadas')} />
        </Table>
      )}

      <div className="ShipmentFormRow">
        <Form.Item label={t('País')} name={'receiver_pais'}>
          <AntdSelect {...paisesSelectProps} allowClear showSearch disabled={isBrasilSelected} />
        </Form.Item>
        {isBrasilSelected ? (
          <Form.Item
            label={postalCodeLabel}
            name={'receiver_postal_code'}
            rules={[
              {
                required: true,
                message: t('El {{field}} es requerido', { field: postalCodeLabel }),
              },
            ]}>
            <Input suffix={cepLookupState.loading && <LoadingOutlined />} />
          </Form.Item>
        ) : (
          <Form.Item label={t('Provincia')} name={'receiver_provincia'}>
            <AntdSelect {...provinciasSelectProps} disabled={!pais} allowClear />
          </Form.Item>
        )}
      </div>
      {isBrasilSelected && cepLookupState.error && (
        <Alert
          style={{ marginBottom: 8 }}
          type="warning"
          showIcon
          message={t('No se pudo encontrar una dirección para el CEP ingresado')}
          action={
            !canEditLocation ? (
              <Button size="small" onClick={enableBrazilManualLocation}>
                {t('Cargar manualmente')}
              </Button>
            ) : (
              <Tag color="warning">{t('Carga manual')}</Tag>
            )
          }
        />
      )}
      <div className="ShipmentFormRow">
        {isBrasilSelected && (
          <Form.Item label={t('Provincia')} name={'receiver_provincia'}>
            <AntdSelect {...provinciasSelectProps} disabled={!canEditLocation} allowClear />
          </Form.Item>
        )}
        <Form.Item
          label={t('Localidad')}
          name={'receiver_localidad'}
          rules={[
            { required: true, message: t('La {{field}} es requerida', { field: t('localidad') }) },
          ]}>
          <AntdSelect
            {...localidadesSelectProps}
            options={localidadesOptions}
            onChange={handleLocalidadChange}
            disabled={!pais || !provincia || !canEditLocation}
            allowClear
            showSearch
          />
        </Form.Item>
        {!isBrasilSelected && (
          <Form.Item
            label={postalCodeLabel}
            name={'receiver_postal_code'}
            rules={[
              {
                required: true,
                message: t('El {{field}} es requerido', { field: postalCodeLabel }),
              },
            ]}>
            <Input />
          </Form.Item>
        )}
      </div>
      <Form.Item
        label={t('Dirección')}
        name={'receiver_address'}
        rules={[
          {
            required: true,
            message: t('La {{field}} es requerida', { field: t('dirección') }),
          },
        ]}>
        <Input />
      </Form.Item>
      <div className="ShipmentFormRow">
        <Form.Item
          label={t('Email')}
          name={'receiver_email'}
          rules={[
            { type: 'email', message: t('Email inválido') },
            { required: true, message: t('El {{field}} es requerido', { field: t('email') }) },
          ]}>
          <Input />
        </Form.Item>
        <Form.Item
          label={t('Número de Teléfono')}
          name={'receiver_phone'}
          validateTrigger="onBlur"
          rules={[
            {
              required: true,
              message: t('El {{field}} es requerido', { field: t('número de teléfono') }),
            },
            {
              validateTrigger: 'onBlur',
              validator: (_, value) => {
                if (!value || !countryCode) {
                  return Promise.resolve();
                }

                const { regexValidation } = phoneNumberMeta[countryCode] || {};

                if (regexValidation && !regexValidation.test(value)) {
                  return Promise.reject(
                    new Error(t('Número de teléfono inválido para el país seleccionado')),
                  );
                }

                return Promise.resolve();
              },
            },
          ]}>
          <Input prefix={phoneNumberMeta[countryCode]?.phoneCountry} />
        </Form.Item>
      </div>
      <div className="ShipmentFormRow">
        <Form.Item
          label={t('Nombre y apellido del destinatario')}
          name={'receiver_name'}
          rules={[
            {
              required: true,
              message: t('El {{field}} es requerido', {
                field: t('Nombre y apellido del destinatario'),
              }),
            },
          ]}>
          <Input />
        </Form.Item>
      </div>
      <div className="ShipmentFormRow">
        <Form.Item
          label={t('Instrucciones para la entrega')}
          tooltip={t('Se imprimirá en la etiqueta de envío')}
          name={'receiver_delivery_instructions'}>
          <Input />
        </Form.Item>
      </div>
    </div>
  );
};

export default ShipmentAddressForm;
